/**
 * Keeps the app shell the size of the part of the screen the browser is actually
 * showing.
 *
 * Garaimon never scrolls: the shell, the screens and the game layouts are all
 * pinned to the viewport. Mobile browsers only collapse their own address bar in
 * response to a scroll, so on a page like this the bar stays on screen. Because
 * the page asks to draw edge to edge (`viewport-fit=cover`), the bar is painted
 * over the game - and on browsers that dock it at the bottom, such as DuckDuckGo
 * on Android and Safari on iPhone, it lands on top of the bottom menu.
 *
 * The `dvh` unit is the usual answer, and it is what the shell used to rely on,
 * but it only tracks the browser's own chrome on browsers that shrink the layout
 * viewport for it. It does not reliably exclude an overlaid bar everywhere, and
 * it never accounts for anything else covering the page.
 *
 * So the shell is measured instead. `window.visualViewport` reports the region
 * the user can actually see - its height excludes the browser's chrome, and its
 * `offsetTop` says where that region starts - and the shell is pinned to exactly
 * that box. The bottom menu then sits above the browser bar whatever the browser
 * does with it, and no scroll runway or bounce is introduced.
 *
 * The arithmetic lives in a small pure function so it can be tested without a
 * browser; only the wiring below touches the DOM.
 */

/** The reading `viewportBox` works from, taken from `window.visualViewport`. */
export interface ViewportReading {
  /** `visualViewport.height`, or `undefined` when the browser has no such API. */
  visualHeight: number | undefined;
  /** `visualViewport.offsetTop`, or `undefined` when the browser has no such API. */
  visualOffsetTop: number | undefined;
  /** `visualViewport.scale`, or `undefined` when the browser has no such API. */
  scale: number | undefined;
  /** `window.innerHeight`, used when the visual viewport says nothing usable. */
  fallbackHeight: number;
}

export interface ViewportBox {
  /** Height to give the app shell, in CSS pixels. */
  height: number;
  /** How far down the visible region starts, in CSS pixels. */
  top: number;
}

/**
 * Above this, `visualViewport` is reporting a pinch zoom rather than a smaller
 * visible area. The two are indistinguishable by height alone: a zoomed-in page
 * has a small visual viewport by design, and treating that as "something is
 * covering the screen" would shrink the app every time a player pinched.
 */
const PINCH_ZOOM_TOLERANCE = 1.01;

/**
 * The box the app shell should occupy.
 *
 * Falls back to the full window when the visual viewport is missing, reports a
 * non-positive height, or is zoomed. `offsetTop` is clamped because it is the
 * distance the visual viewport has been pushed down; a browser that reports a
 * negative offset is saying nothing useful, and a negative `top` would push the
 * shell off the top of the screen.
 */
export function viewportBox(reading: ViewportReading): ViewportBox {
  const { visualHeight, visualOffsetTop, scale, fallbackHeight } = reading;
  if (scale !== undefined && scale > PINCH_ZOOM_TOLERANCE) return { height: fallbackHeight, top: 0 };
  if (visualHeight === undefined || !(visualHeight > 0)) return { height: fallbackHeight, top: 0 };
  return { height: visualHeight, top: Math.max(0, visualOffsetTop ?? 0) };
}

/** The custom properties the shell reads. Named here so the CSS and this file agree. */
const HEIGHT_VARIABLE = '--app-height';
const TOP_VARIABLE = '--app-top';

function readViewport(): ViewportReading {
  // Normalised to `undefined` rather than left as the DOM's `null`, so the
  // diagnostic below can tell "this browser has no visual viewport" from "the
  // browser has one and is reporting a value" with one comparison.
  const viewport = window.visualViewport ?? undefined;
  return {
    visualHeight: viewport?.height,
    visualOffsetTop: viewport?.offsetTop,
    scale: viewport?.scale,
    fallbackHeight: window.innerHeight,
  };
}

function readViewportBox(): ViewportBox {
  return viewportBox(readViewport());
}

/**
 * Starts keeping `--app-height` and `--app-top` in step with the visible area.
 *
 * Writes are coalesced to one per animation frame: a pinch or a keyboard opening
 * fires a resize on every frame, and setting two custom properties that many
 * times would force a layout each time for no gain. The first measurement is
 * applied synchronously so the shell is the right size for its first paint
 * rather than jumping once the frame lands.
 */
export function startViewportTracking(): void {
  const root = document.documentElement;
  let frame = 0;
  const apply = () => {
    frame = 0;
    const box = readViewportBox();
    root.style.setProperty(HEIGHT_VARIABLE, `${Math.round(box.height)}px`);
    root.style.setProperty(TOP_VARIABLE, `${Math.round(box.top)}px`);
  };
  const schedule = () => {
    if (frame) return;
    frame = requestAnimationFrame(apply);
  };
  apply();

  window.addEventListener('resize', schedule);
  window.addEventListener('orientationchange', schedule);
  // The visual viewport changes without the window changing size: the browser's
  // bar sliding in and out on a scroll it performed itself, or the on-screen
  // keyboard opening. Neither fires `window`'s resize on every browser.
  window.visualViewport?.addEventListener('resize', schedule);
  window.visualViewport?.addEventListener('scroll', schedule);
}

/**
 * Shows the raw viewport numbers in the corner, for when the fix above does not
 * take on a particular browser.
 *
 * The readings differ per browser and cannot be reproduced off-device, so this
 * is the only way to tell whether a browser is reporting its own bar as covering
 * part of the page - which decides whether measuring is the right fix at all, or
 * whether the game has to run without browser chrome instead. Switched on by
 * `?devViewport=1`, logged as well as drawn, and left off otherwise.
 */
export function startViewportDiagnostic(): void {
  const readout = document.createElement('div');
  readout.className = 'viewport-diagnostic';
  readout.setAttribute('role', 'status');
  document.body.append(readout);
  let lastLogged = '';
  const paint = () => {
    const reading = readViewport();
    const box = viewportBox(reading);
    const reported = reading.visualHeight === undefined ? 'unavailable' : `${Math.round(reading.visualHeight)} @ ${Math.round(reading.visualOffsetTop ?? 0)}`;
    // How much of the window the shell does not cover once it is pinned to the
    // visible region. A positive number is the browser's own bar sitting on top
    // of the bottom of the window; zero means nothing is being covered.
    const covered = Math.round(window.innerHeight - (box.top + box.height));
    const lines = [
      `innerHeight ${Math.round(window.innerHeight)}`,
      `visualViewport ${reported}`,
      `scale ${reading.scale === undefined ? '-' : reading.scale.toFixed(2)}`,
      `app box ${Math.round(box.height)} @ ${Math.round(box.top)}`,
      `uncovered at bottom ${covered}px`,
    ];
    readout.textContent = lines.join('\n');
    const joined = lines.join(' | ');
    if (joined !== lastLogged) {
      lastLogged = joined;
      console.info('Garaimon viewport', lines.join(', '));
    }
  };
  paint();
  window.addEventListener('resize', paint);
  window.visualViewport?.addEventListener('resize', paint);
  window.visualViewport?.addEventListener('scroll', paint);
}
